import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SrIcon, type SrIconName } from "@/components/ui/SrIcon";
import { SYSTEM_ROLE_OPTIONS, SYSTEM_ROLE_LABELS } from "@/lib/constants";
import {
  useAllSidebarShortcuts,
  type DBSidebarShortcut,
} from "@/hooks/use-sidebar-shortcuts";
import {
  useCreateShortcut,
  useUpdateShortcut,
  useDeleteShortcut,
  useReorderShortcuts,
} from "@/hooks/use-sidebar-shortcuts-mutations";
import { toast } from "sonner";

/**
 * Curated list of icons available in the icon dropdown. Subset of the SrIcon
 * sprite that makes sense for shortcuts (action/navigation/state icons).
 */
const AVAILABLE_ICONS: SrIconName[] = [
  "film",
  "receipt",
  "check-circle",
  "calendar",
  "clipboard-check",
  "clipboard-list",
  "clock",
  "dashboard",
  "file",
  "folder",
  "inbox",
  "mail",
  "money",
  "monitor",
  "search",
  "send",
  "star",
  "target",
  "trending-up",
  "trophy",
  "user",
  "users",
];

interface ShortcutFormState {
  label: string;
  path: string;
  icon: SrIconName;
}

const EMPTY_FORM: ShortcutFormState = {
  label: "",
  path: "/",
  icon: "star",
};

export function SnarveierCard() {
  const [selectedRole, setSelectedRole] = useState<string>(
    SYSTEM_ROLE_OPTIONS[0],
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ShortcutFormState>(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);

  const { data: allShortcuts, isLoading } = useAllSidebarShortcuts();
  const createMut = useCreateShortcut();
  const updateMut = useUpdateShortcut();
  const deleteMut = useDeleteShortcut();
  const reorderMut = useReorderShortcuts();

  const shortcutsForRole = useMemo<DBSidebarShortcut[]>(() => {
    return (allShortcuts ?? [])
      .filter((s) => s.role === selectedRole)
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [allShortcuts, selectedRole]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const handleStartCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const handleStartEdit = (s: DBSidebarShortcut) => {
    setForm({ label: s.label, path: s.path, icon: s.icon });
    setEditingId(s.id);
    setShowForm(true);
  };

  const validateForm = (): string | null => {
    if (!form.label.trim()) return "Etikett er påkrevd";
    if (!form.path.trim()) return "Sti er påkrevd";
    if (!form.path.startsWith("/")) return "Sti må starte med /";
    if (!form.icon.trim()) return "Ikon er påkrevd";
    return null;
  };

  const handleSave = () => {
    const err = validateForm();
    if (err) {
      toast.error(err);
      return;
    }

    if (editingId) {
      updateMut.mutate(
        {
          id: editingId,
          label: form.label.trim(),
          path: form.path.trim(),
          icon: form.icon,
        },
        {
          onSuccess: () => {
            toast.success("Snarvei oppdatert");
            resetForm();
          },
          onError: (e) => toast.error((e as Error).message),
        },
      );
      return;
    }

    const nextSortOrder =
      shortcutsForRole.length > 0
        ? Math.max(...shortcutsForRole.map((s) => s.sort_order)) + 1
        : 1;

    createMut.mutate(
      {
        role: selectedRole,
        label: form.label.trim(),
        path: form.path.trim(),
        icon: form.icon,
        sort_order: nextSortOrder,
      },
      {
        onSuccess: () => {
          toast.success("Snarvei lagt til");
          resetForm();
        },
        onError: (e) => toast.error((e as Error).message),
      },
    );
  };

  const handleDelete = (s: DBSidebarShortcut) => {
    if (!confirm(`Slette "${s.label}"?`)) return;
    deleteMut.mutate(s.id, {
      onSuccess: () => toast.success("Snarvei slettet"),
      onError: (e) => toast.error((e as Error).message),
    });
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= shortcutsForRole.length) return;
    const next = [...shortcutsForRole];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    reorderMut.mutate(
      { orderedIds: next.map((s) => s.id) },
      {
        onError: (e) => toast.error((e as Error).message),
      },
    );
  };

  const handleToggleEnabled = (s: DBSidebarShortcut) => {
    updateMut.mutate(
      { id: s.id, enabled: !s.enabled },
      {
        onSuccess: () =>
          toast.success(s.enabled ? "Snarvei deaktivert" : "Snarvei aktivert"),
        onError: (e) => toast.error((e as Error).message),
      },
    );
  };

  if (isLoading) return null;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <SrIcon name="star" className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-base">Snarveier i sidemeny</h2>
        </div>

        <div className="flex items-end gap-3 mb-4">
          <div className="flex-1">
            <Label htmlFor="snarvei-role">Velg rolle</Label>
            <Select
              value={selectedRole}
              onValueChange={(v) => {
                setSelectedRole(v);
                resetForm();
              }}
            >
              <SelectTrigger id="snarvei-role" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SYSTEM_ROLE_OPTIONS.map((role) => (
                  <SelectItem key={role} value={role}>
                    {SYSTEM_ROLE_LABELS[role]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!showForm && (
            <Button onClick={handleStartCreate} variant="default">
              <SrIcon name="plus" size={14} className="mr-1" />
              Legg til snarvei
            </Button>
          )}
        </div>

        {showForm && (
          <div className="border border-border rounded-md p-4 mb-4 space-y-3 bg-muted/30">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="snarvei-label">Etikett</Label>
                <Input
                  id="snarvei-label"
                  value={form.label}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, label: e.target.value }))
                  }
                  placeholder="F.eks. Aktive prosjekter"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="snarvei-path">Sti</Label>
                <Input
                  id="snarvei-path"
                  value={form.path}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, path: e.target.value }))
                  }
                  placeholder="/prosjekter?status=active"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="snarvei-icon">Ikon</Label>
                <Select
                  value={form.icon}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, icon: v as SrIconName }))
                  }
                >
                  <SelectTrigger id="snarvei-icon" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_ICONS.map((icon) => (
                      <SelectItem key={icon} value={icon}>
                        <span className="flex items-center gap-2">
                          <SrIcon name={icon} size={14} />
                          {icon}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={resetForm}>
                Avbryt
              </Button>
              <Button
                onClick={handleSave}
                disabled={createMut.isPending || updateMut.isPending}
              >
                {editingId ? "Lagre" : "Opprett"}
              </Button>
            </div>
          </div>
        )}

        {shortcutsForRole.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            Ingen snarveier for denne rollen ennå.
          </p>
        ) : (
          <div className="space-y-1">
            {shortcutsForRole.map((s, idx) => (
              <div
                key={s.id}
                className="flex items-center gap-3 border border-border/50 rounded-md px-3 py-2"
              >
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={() => handleMove(idx, -1)}
                    disabled={idx === 0 || reorderMut.isPending}
                    aria-label={`Flytt ${s.label} opp`}
                    className="h-4 w-4 text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <SrIcon name="chevron-up" size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMove(idx, 1)}
                    disabled={
                      idx === shortcutsForRole.length - 1 ||
                      reorderMut.isPending
                    }
                    aria-label={`Flytt ${s.label} ned`}
                    className="h-4 w-4 text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <SrIcon name="chevron-down" size={14} />
                  </button>
                </div>
                <SrIcon name={s.icon} size={16} className="text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{s.label}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {s.path}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggleEnabled(s)}
                  className={
                    "text-xs px-2 py-1 rounded " +
                    (s.enabled
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-gray-100 text-gray-500")
                  }
                  aria-label={s.enabled ? "Deaktiver" : "Aktiver"}
                >
                  {s.enabled ? "Aktiv" : "Skjult"}
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleStartEdit(s)}
                  aria-label={`Rediger ${s.label}`}
                >
                  <SrIcon name="edit" size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(s)}
                  aria-label={`Slett ${s.label}`}
                >
                  <SrIcon name="trash" size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
